import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import DeliveryOption from '../components/DeliveryOption';
import { useCheckout } from '../context/CheckoutContext';
import { getRegionType } from '../utils/deliveryUtils';
import { formatIdrFromUsd } from '../utils/currency';

const ShippingOptions = () => {
  const navigate = useNavigate();
  const { 
    checkoutData, 
    updateDeliveryOptions,
    selectDeliveryMethod,
    getTotalPrice,
    hasRequiredDataForStep 
  } = useCheckout();
  
  const { 
    productId, 
    productName, 
    selectedSize, 
    price, 
    shippingAddress, 
    deliveryMethod,
    availableDeliveryOptions 
  } = checkoutData;
  
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(deliveryMethod);

  // Check if we have the required data for this step
  useEffect(() => {
    if (!hasRequiredDataForStep('shipping-options')) {
      navigate('/checkout/shipping');
      return;
    }

    // Update available delivery options when component mounts
    if (shippingAddress && availableDeliveryOptions.length === 0) {
      updateDeliveryOptions(shippingAddress);
    }
  }, [hasRequiredDataForStep, navigate, shippingAddress, availableDeliveryOptions.length, updateDeliveryOptions]);

  // Handle delivery option selection
  const handleOptionSelect = (optionId) => {
    setSelectedOption(optionId);
    selectDeliveryMethod(optionId);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedOption) {
      alert('Please select a delivery option');
      return;
    }

    setIsLoading(true);
    
    // Small delay for UX
    setTimeout(() => {
      setIsLoading(false);
      // Proceed to payment
      navigate('/checkout/payment');
    }, 500);
  };

  // Get region information for display
  const regionType = shippingAddress ? getRegionType(shippingAddress) : 'unknown';
  const regionInfo = {
    international: {
      title: 'International Shipping',
      description: 'Delivering your rebellion worldwide',
      flag: '🌍'
    },
    indonesia: {
      title: 'Indonesia Delivery',
      description: 'Spreading rebellion across the archipelago',
      flag: '🇮🇩'
    },
    bali: {
      title: 'Bali Delivery & Pickup',
      description: 'Born in Bali - delivered to your doorstep or workshop',
      flag: '🏝️'
    }
  };

  const currentRegion = regionInfo[regionType] || regionInfo.international;

  // Check if we have the necessary data
  if (!productId || !price || !shippingAddress) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">Missing Order Information</h1>
          <p className="text-light mb-8">Please complete the previous steps to continue.</p>
          <Button onClick={() => navigate('/products')} variant="primary">
            Start Over
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Checkout Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">1</div>
                <span className="text-gray-400 text-sm mt-2">Confirmation</span>
              </div>
              <div className="flex-1 h-1 bg-gray-700 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">2</div>
                <span className="text-gray-400 text-sm mt-2">Shipping</span>
              </div>
              <div className="flex-1 h-1 bg-primary mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-primary text-dark rounded-full flex items-center justify-center font-bold">3</div>
                <span className="text-primary text-sm mt-2">Delivery</span>
              </div>
              <div className="flex-1 h-1 bg-gray-700 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">4</div>
                <span className="text-gray-400 text-sm mt-2">Payment</span>
              </div>
            </div>
          </div>
          
          <h1 className="font-serif text-3xl font-bold text-primary mb-2">
            Choose Your Delivery Method
          </h1>
          <p className="text-gray-400 mb-8">
            How would you like to receive your artifact of rebellion?
          </p>

          {/* Region Information */}
          <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-8">
            <div className="flex items-center mb-4">
              <span className="text-3xl mr-3">{currentRegion.flag}</span>
              <div>
                <h2 className="text-xl font-semibold text-primary">
                  {currentRegion.title}
                </h2>
                <p className="text-gray-400 text-sm">
                  {currentRegion.description}
                </p>
              </div>
            </div>
            
            <div className="text-sm text-gray-300">
              <strong>Shipping to:</strong> {shippingAddress.fullName}<br />
              {shippingAddress.addressLine1}, {shippingAddress.city}, {shippingAddress.state}, {shippingAddress.country}
              <button 
                onClick={() => navigate('/checkout/shipping')}
                className="ml-4 text-primary hover:text-primary/80 underline"
              >
                Change Address
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Delivery Options */}
            <div className="space-y-4 mb-8">
              {availableDeliveryOptions.length > 0 ? (
                availableDeliveryOptions.map((option) => (
                  <DeliveryOption
                    key={option.id}
                    option={option}
                    isSelected={selectedOption === option.id}
                    onSelect={handleOptionSelect}
                    disabled={isLoading}
                  />
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-gray-400">Loading available delivery options...</p>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => navigate('/checkout/shipping')}
                disabled={isLoading}
              >
                Back to Shipping
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={!selectedOption || isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dark mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  'Continue to Payment'
                )}
              </Button>
            </div>
          </form>

          {/* Order Summary */}
          <div className="mt-8 bg-secondary border border-gray-800 rounded-lg overflow-hidden">
            <div className="p-6">
              <h2 className="font-serif text-xl font-bold text-primary mb-4">
                Order Summary
              </h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Product</span>
                  <span className="text-light">{productName}</span>
                </div>
                {selectedSize && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Size</span>
                    <span className="text-light">{selectedSize}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-light">{formatIdrFromUsd(price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">
                    Delivery {selectedOption && availableDeliveryOptions.length > 0 
                      ? `(${availableDeliveryOptions.find(opt => opt.id === selectedOption)?.name || selectedOption})` 
                      : ''
                    }
                  </span>
                  <span className="text-light">
                    {selectedOption && availableDeliveryOptions.length > 0
                      ? availableDeliveryOptions.find(opt => opt.id === selectedOption)?.cost === 0
                        ? 'Free'
                        : formatIdrFromUsd(availableDeliveryOptions.find(opt => opt.id === selectedOption)?.cost)
                      : 'Select delivery method'
                    }
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-800 mt-2">
                  <span className="text-light">Total</span>
                  <span className="text-primary">
                    {formatIdrFromUsd(getTotalPrice())}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Outlaw Souls messaging */}
          <div className="mt-8 text-center">
            <p className="text-primary italic">
              "Every choice shapes your journey. Choose how your rebellion arrives."
            </p>
            <p className="text-gray-500 text-sm mt-2">
              — SASTRAJIWA & the Outlaw Souls
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShippingOptions;
